/**
 * Change Allocation Modal
 * Allows changing the inventory item allocation for a booking item.
 * Supports swapping items between bookings, buffer-conflict resolution,
 * and future barcode scanning.
 */

import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Package, Loader2, Search, AlertTriangle, ArrowRightLeft, Check, Clock, Truck } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useBarcodeScanning, useScanHandler } from '../contexts/ScanContext';
import { insertAuditLog } from '../services/bookingAuditLog.service';
import {
  callChangeAllocation,
  formatAllocationFailure,
  type AllocationRpcResult,
} from '../lib/allocationRpc';

interface InventoryItem {
  id: string;
  nr: number | null;
  item_number: string | null;
  combination_code: string | null;
  size: string | null;
  active: boolean;
  allocation_order: number | null;
  barcode?: string | null;
}

interface BookingConflict {
  booking_id: string;
  order_id: string;
  customer_name: string;
  start_date: string;
  end_date: string;
}

interface InventoryItemWithStatus extends InventoryItem {
  status: 'available' | 'booked' | 'buffer_conflict' | 'inactive' | 'current';
  conflict?: BookingConflict;
  conflictBookingItemId?: string;
}

// Translate unexpected exceptions (auth / not-found / network) into a generic message.
// Domain-level failures come back as structured ok:false and are handled via
// formatAllocationFailure.
function mapAllocationError(
  _err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string
): string {
  return t('changeAllocation.failedToChange');
}

interface ChangeAllocationModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingId: string;
  bookingItemId: string;
  currentInventoryItemId: string | null;
  productTypeId: string;
  productName: string;
  size: string | null;
  locationId: string;
  merchantId: string;
  startDate: string;
  endDate: string;
  isTransferItem?: boolean;
  homeLocationName?: string;
  /** Inventory item IDs already used by other items in the same booking (to exclude from swap options) */
  siblingInventoryItemIds?: string[];
  onAllocationChanged: (
    newInventoryItemId: string,
    newInventoryItemData: { nr: number | null; item_number: string | null; combination_code: string | null; size: string | null },
    swappedBookingItemId?: string
  ) => void;
}

export default function ChangeAllocationModal({
  isOpen,
  onClose,
  bookingId,
  bookingItemId,
  currentInventoryItemId,
  productTypeId,
  productName,
  size,
  locationId,
  merchantId,
  startDate,
  endDate,
  isTransferItem,
  homeLocationName,
  siblingInventoryItemIds = [],
  onAllocationChanged
}: ChangeAllocationModalProps) {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [inventoryItems, setInventoryItems] = useState<InventoryItemWithStatus[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [bufferAction, setBufferAction] = useState<'ignore' | 'swap'>('ignore');
  const [preview, setPreview] = useState<AllocationRpcResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { enabled: scanningEnabled, feedback } = useBarcodeScanning();

  useEffect(() => {
    if (isOpen) {
      loadInventoryItems();
      setSearchQuery('');
      setSelectedItemId(null);
      setBufferAction('ignore');
      setError(null);
    }
  }, [isOpen, productTypeId, locationId, startDate, endDate]);

  // Reset buffer action when selection changes
  useEffect(() => {
    setBufferAction('ignore');
  }, [selectedItemId]);

  async function loadInventoryItems() {
    setLoading(true);
    setError(null);

    try {
      // Fetch inventory items and buffer configuration in parallel.
      // Items can belong to a product type via product_type_id, alt_product_type_id,
      // or additional_product_type_ids — match all three (same as check_availability_enhanced).
      const [itemsResult, productResult] = await Promise.all([
        supabase
          .from('inventory_items')
          .select(scanningEnabled
            ? 'id, nr, item_number, combination_code, size, active, allocation_order, barcode'
            : 'id, nr, item_number, combination_code, size, active, allocation_order')
          .or(`product_type_id.eq.${productTypeId},alt_product_type_id.eq.${productTypeId},additional_product_type_ids.cs.{"${productTypeId}"}`)
          .order('allocation_order', { ascending: true, nullsFirst: false })
          .order('nr', { ascending: true }),
        supabase
          .from('product_types')
          .select('maintenance_time_hours')
          .eq('id', productTypeId)
          .single() as unknown as Promise<{ data: { maintenance_time_hours: number | null } | null; error: any }>
      ]);

      if (itemsResult.error) throw itemsResult.error;
      const items = itemsResult.data as unknown as InventoryItem[] | null;

      // Maintenance buffer applied on both sides, matching check_availability_enhanced.
      const maintenanceHours = productResult.data?.maintenance_time_hours || 0;
      const bufferMs = maintenanceHours * 60 * 60 * 1000;

      // Only open/active bookings block allocation — matches check_availability_enhanced.
      const { data: conflicts, error: conflictsError } = await supabase
        .from('booking_items')
        .select(`
          id,
          inventory_item_id,
          bookings!inner(
            id,
            order_id,
            customer_firstname,
            customer_lastname,
            start_date,
            end_date,
            booking_state
          )
        `)
        .in('inventory_item_id', (items || []).map((i: any) => i.id))
        .in('bookings.booking_state', ['open', 'active']);

      if (conflictsError) throw conflictsError;

      const ourStart = new Date(startDate);
      const ourEnd = new Date(endDate);

      // Build conflict map with buffer-aware overlap detection
      const conflictMap = new Map<string, {
        bookingItemId: string;
        conflict: BookingConflict;
        isDirectOverlap: boolean;
      }>();

      for (const conflict of (conflicts || []) as any[]) {
        const booking = conflict.bookings;
        if (!booking) continue;
        if (conflict.id === bookingItemId) continue;
        if (!conflict.inventory_item_id) continue;

        const conflictStart = new Date(booking.start_date);
        const conflictEnd = new Date(booking.end_date);

        // Direct date overlap (no buffer)
        const isDirectOverlap = conflictStart < ourEnd && conflictEnd > ourStart;

        // Buffer-extended overlap — single-sided maintenance buffer applied
        // after each booking's end_date, matching check_booking_overlap() trigger
        // and check_availability_enhanced RPC.
        const isBufferOverlap =
          conflictStart.getTime() < ourEnd.getTime() + bufferMs &&
          conflictEnd.getTime() + bufferMs > ourStart.getTime();

        if (isDirectOverlap || isBufferOverlap) {
          // Keep the "worst" conflict per item (direct > buffer-only)
          const existing = conflictMap.get(conflict.inventory_item_id);
          if (!existing || (isDirectOverlap && !existing.isDirectOverlap)) {
            conflictMap.set(conflict.inventory_item_id, {
              bookingItemId: conflict.id,
              conflict: {
                booking_id: booking.id,
                order_id: booking.order_id,
                customer_name: `${booking.customer_firstname || ''} ${booking.customer_lastname || ''}`.trim() || 'Unknown',
                start_date: booking.start_date,
                end_date: booking.end_date
              },
              isDirectOverlap
            });
          }
        }
      }

      // Determine status for each item
      const itemsWithStatus: InventoryItemWithStatus[] = (items || []).map(item => {
        if (item.id === currentInventoryItemId) {
          return { ...item, status: 'current' as const };
        }
        if (!item.active) {
          return { ...item, status: 'inactive' as const };
        }
        const conflictInfo = conflictMap.get(item.id);
        if (conflictInfo) {
          return {
            ...item,
            status: conflictInfo.isDirectOverlap ? 'booked' as const : 'buffer_conflict' as const,
            conflict: conflictInfo.conflict,
            conflictBookingItemId: conflictInfo.bookingItemId
          };
        }
        return { ...item, status: 'available' as const };
      });

      setInventoryItems(itemsWithStatus);
    } catch (err) {
      console.error('Error loading inventory items:', err);
      setError(t('changeAllocation.failedToLoadItems'));
    } finally {
      setLoading(false);
    }
  }

  // Filter items based on search query, and exclude sibling items from the same booking
  const filteredItems = useMemo(() => {
    const siblingSet = new Set(siblingInventoryItemIds);
    let result = inventoryItems.filter(item =>
      // Never show items already assigned to other items in the same booking
      item.status === 'current' || !siblingSet.has(item.id)
    );

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.item_number?.toLowerCase().includes(query) ||
        item.combination_code?.toLowerCase().includes(query) ||
        item.nr?.toString().includes(query) ||
        item.size?.toLowerCase().includes(query)
      );
    }

    return result;
  }, [inventoryItems, searchQuery, siblingInventoryItemIds]);

  // Scanning an item selects it as the new allocation (scanning merchants).
  // Its status (available / swap / buffer) is then handled exactly as a click.
  useScanHandler((code) => {
    if (loading) return; // list not there yet; the scan would look like a wrong product
    const match = inventoryItems.find(item => item.barcode === code);
    if (!match) {
      feedback('error');
      setError(t('scan.notThisProduct', { code, product: productName }));
      return;
    }
    if (match.status === 'current') {
      feedback('ok');
      setError(t('scan.alreadyAllocatedHere'));
      return;
    }
    if (match.status === 'inactive') {
      feedback('error');
      setError(t('changeAllocation.itemInactive'));
      return;
    }
    if (siblingInventoryItemIds.includes(match.id)) {
      feedback('error');
      setError(t('scan.alreadyInBooking'));
      return;
    }
    feedback('ok');
    setError(null);
    setSearchQuery('');
    setSelectedItemId(match.id);
  }, isOpen);

  // Get current item info
  const currentItem = inventoryItems.find(item => item.id === currentInventoryItemId);
  const currentItemInactive = currentItem && !currentItem.active;

  // Get selected item info
  const selectedItem = selectedItemId ? inventoryItems.find(item => item.id === selectedItemId) : null;
  const isSwap = selectedItem?.status === 'booked';
  const isBufferConflict = selectedItem?.status === 'buffer_conflict';
  const willSwap = isSwap || (isBufferConflict && bufferAction === 'swap');

  // Reset preview when nothing to dry-run (or switching away from a swap)
  useEffect(() => {
    if (!selectedItem || !willSwap) {
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
    }
  }, [selectedItemId, willSwap, selectedItem]);

  // Dry-run on selection change so the modal can surface reverse-leg conflicts
  // and target multi-conflicts before the user commits.
  useEffect(() => {
    if (!selectedItem || !willSwap || !selectedItemId) return;

    let cancelled = false;
    setPreviewLoading(true);
    setPreview(null);
    setPreviewError(null);

    (async () => {
      try {
        const result = await callChangeAllocation({
          p_booking_item_id: bookingItemId,
          p_new_inventory_item_id: selectedItemId,
          p_swap_with_booking_item_id: selectedItem.conflictBookingItemId || null,
          p_current_inventory_item_id: currentInventoryItemId,
          p_skip_buffer_check: false,
          p_dry_run: true,
        });
        if (cancelled) return;
        setPreview(result);
        if (!result.ok) setPreviewError(formatAllocationFailure(result, t));
      } catch (err) {
        if (cancelled) return;
        console.error('Dry-run failed:', err);
        setPreviewError(t('changeAllocation.failedToChange'));
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedItemId,
    willSwap,
    bookingItemId,
    currentInventoryItemId,
    selectedItem?.conflictBookingItemId,
  ]);

  async function handleConfirm() {
    if (!selectedItemId) return;

    setSaving(true);
    setError(null);

    try {
      const selectedItemData = inventoryItems.find(item => item.id === selectedItemId) as InventoryItemWithStatus;

      const newItemData = {
        nr: selectedItemData?.nr ?? null,
        item_number: selectedItemData?.item_number ?? null,
        combination_code: selectedItemData?.combination_code ?? null,
        size: selectedItemData?.size ?? null
      };

      let swapBookingItemId: string | undefined;
      let rpcParams: Parameters<typeof callChangeAllocation>[0];

      if (selectedItemData?.status === 'booked') {
        // Direct overlap → always swap
        rpcParams = {
          p_booking_item_id: bookingItemId,
          p_new_inventory_item_id: selectedItemId,
          p_swap_with_booking_item_id: selectedItemData.conflictBookingItemId || null,
          p_current_inventory_item_id: currentInventoryItemId,
          p_skip_buffer_check: false,
        };
        swapBookingItemId = selectedItemData.conflictBookingItemId;

      } else if (selectedItemData?.status === 'buffer_conflict') {
        if (bufferAction === 'swap') {
          // Buffer conflict → swap items (no buffer bypass needed, swap resolves it)
          rpcParams = {
            p_booking_item_id: bookingItemId,
            p_new_inventory_item_id: selectedItemId,
            p_swap_with_booking_item_id: selectedItemData.conflictBookingItemId || null,
            p_current_inventory_item_id: currentInventoryItemId,
            p_skip_buffer_check: false,
          };
          swapBookingItemId = selectedItemData.conflictBookingItemId;
        } else {
          // Buffer conflict → ignore buffer (simple reassignment, bypass buffer check)
          rpcParams = {
            p_booking_item_id: bookingItemId,
            p_new_inventory_item_id: selectedItemId,
            p_swap_with_booking_item_id: null,
            p_current_inventory_item_id: null,
            p_skip_buffer_check: true,
          };
        }

      } else {
        // Available item → simple reassignment
        rpcParams = {
          p_booking_item_id: bookingItemId,
          p_new_inventory_item_id: selectedItemId,
          p_swap_with_booking_item_id: null,
          p_current_inventory_item_id: null,
          p_skip_buffer_check: false,
        };
      }

      const result = await callChangeAllocation(rpcParams);
      if (!result.ok) {
        setError(formatAllocationFailure(result, t));
        return;
      }

      // Log the allocation change to the audit log (fire-and-forget)
      const currentItemLabel = currentItem?.item_number || (currentItem?.nr ? `#${currentItem.nr}` : null);
      const newItemLabel = selectedItemData?.item_number || (selectedItemData?.nr ? `#${selectedItemData.nr}` : null);
      const wasSwap = !!swapBookingItemId;
      insertAuditLog({
        bookingId,
        merchantId,
        changeType: 'inventory_reallocated',
        changeDetails: {
          product_name: productName,
          from_item: currentItemLabel,
          to_item: newItemLabel,
          swap: wasSwap,
          ...(wasSwap && selectedItemData?.conflict ? {
            swapped_with_booking_order_id: selectedItemData.conflict.order_id,
          } : {}),
        },
        userId: user?.id,
        userEmail: profile?.full_name || user?.email || null,
        triggeredBy: 'admin_panel',
      });

      // Mirror audit entry on the swapped-with booking so its operator sees
      // who moved its item and why.
      if (wasSwap && selectedItemData?.conflict) {
        insertAuditLog({
          bookingId: selectedItemData.conflict.booking_id,
          merchantId,
          changeType: 'inventory_reallocated',
          changeDetails: {
            product_name: productName,
            from_item: newItemLabel,
            to_item: currentItemLabel,
            swap: true,
            swapped_with_booking_order_id: null,
            initiated_by_other_booking: true,
          },
          userId: user?.id,
          userEmail: profile?.full_name || user?.email || null,
          triggeredBy: 'admin_panel',
        });
      }

      onAllocationChanged(selectedItemId, newItemData, swapBookingItemId);
      onClose();
    } catch (err) {
      console.error('Error changing allocation:', err);
      setError(mapAllocationError(err, t));
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 isolate">
      <div
        className="rounded-lg shadow-2xl w-full overflow-hidden flex flex-col mx-auto relative z-50 bg-primary"
        style={{ maxHeight: '90vh', maxWidth: '560px' }}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-md sm:p-lg border-b border-primary bg-primary">
          <div className="flex items-center gap-md">
            <ArrowRightLeft className="w-5 h-5 sm:w-6 sm:h-6 text-blue flex-shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-primary">{t('changeAllocation.title')}</h2>
              <p className="text-sm text-secondary flex items-center gap-xs">
                {productName}{size ? ` - ${size}` : ''}
                {isTransferItem && (
                  <span title={`${t('bookingItems.homeLocation')}: ${homeLocationName || t('bookingItems.anotherLocation')} — ${t('bookingItems.transferRequired')}`}>
                    <Truck className="w-4 h-4 text-danger flex-shrink-0" />
                  </span>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn-icon flex-shrink-0"
            title={t('changeAllocation.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-md sm:p-lg">
          {error && (
            <div className="p-md bg-danger/10 border-l-4 border-danger rounded-lg mb-lg">
              <p className="text-sm text-danger">{error}</p>
            </div>
          )}

          {/* Current Allocation Warning */}
          {currentItemInactive && (
            <div className="p-md bg-warning/10 border-l-4 border-warning rounded-lg mb-lg">
              <div className="flex items-start gap-sm">
                <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-warning">{t('changeAllocation.currentItemInactive')}</p>
                  <p className="text-sm text-secondary mt-xs">
                    {t('changeAllocation.currentItemInactiveDescription', { item: currentItem?.item_number || `#${currentItem?.nr}` })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Allocation */}
          {currentItem && (
            <div className="mb-lg">
              <h3 className="text-sm font-semibold text-secondary mb-sm">{t('changeAllocation.currentAllocation')}</h3>
              <div className="p-md bg-blue/10 border border-blue/20 rounded-lg">
                <div className="flex items-center gap-md">
                  <Package className="w-5 h-5 text-blue" />
                  <div>
                    <span className="font-medium text-primary">
                      {currentItem.item_number || `${t('changeAllocation.item')} #${currentItem.nr}`}
                    </span>
                    {currentItem.combination_code && (
                      <span className="text-sm text-secondary ml-md">
                        {t('changeAllocation.code')}: {currentItem.combination_code}
                      </span>
                    )}
                    {currentItem.size && (
                      <span className="text-sm text-secondary ml-md">
                        {t('changeAllocation.sizeLabel')}: {currentItem.size}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search Field */}
          <div className="mb-lg">
            <div className="relative">
              <Search className="absolute left-md top-1/2 -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('changeAllocation.searchPlaceholder')}
                className="input w-full pl-10"
                autoFocus
              />
            </div>
            <p className="text-xs text-secondary mt-xs">
              {scanningEnabled ? t('scan.allocationScanHint') : t('changeAllocation.searchHint')}
            </p>
          </div>

          {/* Available Items */}
          <div>
            <h3 className="text-sm font-semibold text-secondary mb-sm">
              {t('changeAllocation.availableItems')} ({filteredItems.filter(i => i.status !== 'current').length})
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-xl">
                <Loader2 className="w-8 h-8 text-blue animate-spin" />
                <span className="ml-md text-secondary">{t('changeAllocation.loadingItems')}</span>
              </div>
            ) : filteredItems.filter(i => i.status !== 'current').length === 0 ? (
              <div className="text-center py-xl">
                <Package className="w-12 h-12 text-secondary mx-auto mb-md opacity-50" />
                <p className="text-secondary">{t('changeAllocation.noOtherItems')}</p>
              </div>
            ) : (
              <div className="space-y-sm">
                {filteredItems
                  .filter(item => item.status !== 'current')
                  .map(item => {
                    const isSelected = selectedItemId === item.id;
                    const isDisabled = item.status === 'inactive';
                    const canSelect = !isDisabled;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => canSelect && setSelectedItemId(isSelected ? null : item.id)}
                        disabled={isDisabled}
                        className={`w-full p-sm sm:p-md rounded-lg border transition-all text-left ${
                          isSelected
                            ? 'border-blue bg-blue/10 ring-2 ring-blue'
                            : isDisabled
                              ? 'border-secondary/30 bg-secondary/10 opacity-50 cursor-not-allowed'
                              : item.status === 'booked'
                                ? 'border-warning/30 bg-warning/5 hover:border-warning hover:bg-warning/10'
                                : item.status === 'buffer_conflict'
                                  ? 'border-info/30 bg-info/5 hover:border-info hover:bg-info/10'
                                  : 'border-primary hover:border-blue hover:bg-blue/5'
                        }`}
                      >
                        <div className="flex items-center gap-sm sm:gap-md">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isSelected
                              ? 'bg-blue text-white'
                              : item.status === 'available'
                                ? 'bg-success/20 text-success'
                                : item.status === 'booked'
                                  ? 'bg-warning/20 text-warning'
                                  : item.status === 'buffer_conflict'
                                    ? 'bg-info/20 text-info'
                                    : 'bg-secondary/20 text-secondary'
                          }`}>
                            {isSelected ? (
                              <Check className="w-4 h-4" />
                            ) : item.status === 'booked' ? (
                              <ArrowRightLeft className="w-4 h-4" />
                            ) : item.status === 'buffer_conflict' ? (
                              <Clock className="w-4 h-4" />
                            ) : (
                              <Package className="w-4 h-4" />
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-sm flex-wrap">
                              <span className="font-medium text-primary text-sm">
                                {item.item_number || `${t('changeAllocation.item')} #${item.nr}`}
                              </span>
                              {item.size && (
                                <span className="text-xs text-secondary">
                                  ({item.size})
                                </span>
                              )}
                              {item.status === 'available' && (
                                <span className="text-xs px-sm py-0.5 rounded-full bg-success/20 text-success">
                                  {t('changeAllocation.available')}
                                </span>
                              )}
                              {item.status === 'booked' && (
                                <span className="text-xs px-sm py-0.5 rounded-full bg-warning/20 text-warning">
                                  {t('changeAllocation.swap')}
                                </span>
                              )}
                              {item.status === 'buffer_conflict' && (
                                <span className="text-xs px-sm py-0.5 rounded-full bg-info/20 text-info">
                                  {t('changeAllocation.buffer')}
                                </span>
                              )}
                              {item.status === 'inactive' && (
                                <span className="text-xs px-sm py-0.5 rounded-full bg-secondary/20 text-secondary">
                                  {t('common.inactive')}
                                </span>
                              )}
                              {size && item.size && item.size === size && (
                                <span className="text-xs px-sm py-0.5 rounded-full bg-success/20 text-success font-medium">
                                  {t('changeAllocation.sameSize')}
                                </span>
                              )}
                              {size && item.size && item.size !== size && (
                                <span className="text-xs px-sm py-0.5 rounded-full bg-warning/20 text-warning font-medium">
                                  {t('changeAllocation.differentSize')}
                                </span>
                              )}
                            </div>
                            {item.combination_code && (
                              <p className="text-xs sm:text-sm text-secondary truncate">{t('changeAllocation.code')}: {item.combination_code}</p>
                            )}
                            {item.status === 'booked' && item.conflict && (
                              <p className="text-xs text-warning mt-xs truncate">
                                {t('changeAllocation.bookedBy')} {item.conflict.customer_name} ({item.conflict.order_id})
                              </p>
                            )}
                            {item.status === 'buffer_conflict' && item.conflict && (
                              <p className="text-xs text-info mt-xs truncate">
                                {t('changeAllocation.bufferOverlapWith')} {item.conflict.customer_name} ({item.conflict.order_id})
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Buffer Conflict Action Choice */}
          {isBufferConflict && selectedItem && (
            <div className="mt-lg p-md bg-info/10 border border-info/30 rounded-lg">
              <div className="flex items-start gap-sm mb-md">
                <Clock className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-info">{t('changeAllocation.maintenanceBufferOverlap')}</p>
                  <p className="text-sm text-secondary mt-xs">
                    {t('changeAllocation.bufferOverlapDescription', { orderId: selectedItem.conflict?.order_id })}
                  </p>
                </div>
              </div>
              <div className="space-y-sm">
                <label
                  className={`flex items-start gap-md p-sm rounded-lg cursor-pointer transition-all ${
                    bufferAction === 'ignore'
                      ? 'bg-primary border border-info shadow-sm'
                      : 'hover:bg-secondary border border-transparent'
                  }`}
                >
                  <input
                    type="radio"
                    name="bufferAction"
                    checked={bufferAction === 'ignore'}
                    onChange={() => setBufferAction('ignore')}
                    className="mt-1"
                  />
                  <div>
                    <p className="font-medium text-primary text-sm">{t('changeAllocation.ignoreBuffer')}</p>
                    <p className="text-xs text-secondary">
                      {t('changeAllocation.ignoreBufferDescription')}
                    </p>
                  </div>
                </label>
                {currentInventoryItemId && (
                  <label
                    className={`flex items-start gap-md p-sm rounded-lg cursor-pointer transition-all ${
                      bufferAction === 'swap'
                        ? 'bg-primary border border-info shadow-sm'
                        : 'hover:bg-secondary border border-transparent'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bufferAction"
                      checked={bufferAction === 'swap'}
                      onChange={() => setBufferAction('swap')}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-medium text-primary text-sm">{t('changeAllocation.swapItems')}</p>
                      <p className="text-xs text-secondary">
                        {t('changeAllocation.swapItemsDescription', { orderId: selectedItem.conflict?.order_id })}
                      </p>
                    </div>
                  </label>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-primary p-md sm:p-lg bg-primary space-y-sm">
          {/* Swap preview: dry-run result before the user commits */}
          {willSwap && selectedItem && previewLoading && (
            <div className="flex items-center gap-sm text-sm text-secondary">
              <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
              <span>{t('changeAllocation.checkingSwap')}</span>
            </div>
          )}
          {willSwap && selectedItem && !previewLoading && preview?.ok && selectedItem.conflict && (
            <div className="flex items-center gap-sm text-sm text-success">
              <Check className="w-4 h-4 flex-shrink-0" />
              <span>
                {t('changeAllocation.swapReady', {
                  customerName: selectedItem.conflict.customer_name,
                  orderId: selectedItem.conflict.order_id,
                })}
              </span>
            </div>
          )}
          {willSwap && selectedItem && !previewLoading && previewError && (
            <div className="flex items-start gap-sm text-sm text-danger">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{previewError}</span>
            </div>
          )}
          {/* Non-swap status message (buffer-conflict with ignore action) */}
          {isBufferConflict && selectedItem && bufferAction === 'ignore' && (
            <div className="flex items-center gap-sm text-sm text-info">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span>{t('changeAllocation.bufferReduced')}</span>
            </div>
          )}
          {/* Buttons */}
          <div className="flex items-center justify-end gap-sm">
            <button
              onClick={onClose}
              className="btn btn-ghost"
              disabled={saving}
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={
                !selectedItemId ||
                saving ||
                (willSwap && (previewLoading || !preview?.ok))
              }
              className="btn btn-primary"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {willSwap ? t('changeAllocation.swapping') : t('common.saving')}
                </>
              ) : (
                willSwap
                  ? t('changeAllocation.swapItems')
                  : isBufferConflict
                    ? t('changeAllocation.assignIgnoreBuffer')
                    : t('changeAllocation.title')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
